import React, { useState, Suspense } from 'react';
import classNames from 'classnames';
import useFetch from 'components/shared/hooks/useFetch';
import { routes } from 'constants/config';
import Loadable from 'components/shared/loadable';
import { BetaFeedback } from 'components/BetaProgram/BetaFeedback';
import EMAILS from 'constants/emails';
import { getQueryParam, setQueryStringForCurrentUrl } from 'utils/url';
import ExpandableList, {
  ExpandableListSection,
  ExpandableListChild,
} from 'ui/ExpandableList';
import { getComponentClassHelper } from 'ui/helpers';
import { useFeatureFlags } from 'components/shared/hooks';
import V2Banner from './V2Banner';

export const componentClass = getComponentClassHelper('InsightsV2');

const DiscBitsDashboard = React.lazy(() =>
  import(/* webpackChunkName: "DiscBitsDashboard" */ './DiscBitsDashboard'),
);

const InsightsV2 = ({ dashboardList, embeddedInsightsMultipleDashboards }) => {
  const { data = {}, loading, runRequest } = useFetch(routes.INSIGHTS.V2);
  const dashboardQuery = getQueryParam(window.location.href, 'dashboard');

  const isValidDashboardType = dashboardType =>
    dashboardType && Object.keys(dashboardList).some(d => d === dashboardType);

  const [currentDashboard, setCurrentDashboard] = useState(
    isValidDashboardType(dashboardQuery) ? dashboardQuery : 'default',
  );

  const hasLearningRebrandFlag = useFeatureFlags(['learning-rebrand']);

  const selectDashboardByType = dashboardType => {
    runRequest();
    setCurrentDashboard(dashboardType);
    setQueryStringForCurrentUrl({ dashboard: dashboardType });
  };

  const displayDashboard = () => {
    const DefaultDash = (
      <DiscBitsDashboard dashboardId={dashboardList.default.id} data={data} />
    );

    if (!embeddedInsightsMultipleDashboards) {
      return DefaultDash;
    }

    switch (currentDashboard) {
      case 'readiness':
        return (
          <DiscBitsDashboard
            dashboardId={dashboardList.readiness.id}
            data={data}
          />
        );
      case 'default':
      default:
        return DefaultDash;
    }
  };

  return (
    <div
      className={classNames(componentClass('page'), {
        withSidebar: embeddedInsightsMultipleDashboards,
      })}
    >
      <div className={componentClass('top')}>
        <V2Banner />
        <BetaFeedback
          recipient={EMAILS.INSIGHTS_FEEDBACK}
          betaFeatureName="Embedded Insights Page"
          customClassName={componentClass('beta-feedback')}
        />
      </div>
      {embeddedInsightsMultipleDashboards && (
        <div className={componentClass('sidebar')}>
          <ExpandableList>
            <ExpandableListSection
              header={`< ${
                hasLearningRebrandFlag
                  ? t('insights.v2.sidebar.seismic_managed')
                  : t('insights.v2.sidebar.lessonly_managed')
              } >`}
            >
              {Object.keys(dashboardList).map(dashboardType => {
                return (
                  <ExpandableListChild.Button
                    key={dashboardType}
                    onClick={() => selectDashboardByType(dashboardType)}
                    active={currentDashboard === dashboardType}
                  >
                    {t(`insights.v2.dashboards.${dashboardType}`)}
                  </ExpandableListChild.Button>
                );
              })}
            </ExpandableListSection>
          </ExpandableList>
        </div>
      )}
      <Loadable loading={loading}>
        {!loading && <Suspense fallback={null}>{displayDashboard()}</Suspense>}
      </Loadable>
    </div>
  );
};

export default ({ ...props }) => <InsightsV2 {...props} />;
